/**
 * 接口HTTP成功率、业务成功率成功统计上报（Raptor自定义指标）
 *
 * 主要用于核心业务链路监控，以拦截器形式实现，对业务代码无侵入
 *
 * 上报到Raptor时每个指标会默认带两个tag:
 * - http_success: HTTP是否成功  1成功  0失败
 * - business_code: 业务状态码，如实上报
 */

import { AxiosResponse } from 'axios';
import { isObject } from '@utiljs/is';

// eslint-disable-next-line import/no-cycle
import { reportCustomMetrics } from '@utils/owlReporter';

/**
 * 单个API自定义指标上报配置
 */
type API_REPORT_CONFIG = {
    /**
     * 上报采样率 取值区间[0, 1]。 eg: 0.3 表示**随机**30%概率上报
     * 不确定如何设置采样率？ 可以去Raptor上查询相应接口过去7天的平均调用量，单天调用量 < 30w 可以全量上报。超过 30w 可以咨询 wangge09
     */
    sampleRate: number;
    /** 指标Key */
    metricsKey: string;
    /** 其他业务需要上报的自定义Tag */
    customTags?: { [key: string]: string };
};

/**
 * 采集接口配置对象，key为接口path，value为上报配置
 */
const REPORT_CONFIG_MAP: { [key: string]: API_REPORT_CONFIG } = {
    // 自己送餐
    '/v2/order/receive/processed/w/sent/v2': {
        sampleRate: 1,
        metricsKey: 'SEND_BY_SELF_API',
    },
    // 跑腿非灰度询价
    '/v2/order/receive/processed/r/distribute/shippingFee/v2': {
        sampleRate: 1,
        metricsKey: 'QUERY_PRICE_API',
    },
    // 美配、跑腿非灰度 发配送
    '/v2/order/receive/processed/w/distribute/v2': {
        sampleRate: 1,
        metricsKey: 'SEND_DELIVERY_API',
    },
    // 跑腿灰度询价
    '/v2/order/receive/query/r/pt/activity/info': {
        sampleRate: 1,
        metricsKey: 'PT_GRAY_QUERY_PRICE_API',
    },
    // 跑腿灰度 发配送
    '/v2/order/receive/processed/w/pt/distribute': {
        sampleRate: 1,
        metricsKey: 'PT_GRAY_SEND_DELIVERY_API',
    },
    // 美配转自配
    '/v2/order/receive/processed/w/distribute/changeToPoiSelfShipping': {
        sampleRate: 1,
        metricsKey: 'CHANGE_TO_SELF_DELIVERY_API',
    },
    // 查取消配送原因列表
    '/v2/order/receive/processed/r/distribute/cancel/reason/v2': {
        sampleRate: 1,
        metricsKey: 'QUERY_CANCEL_DELIVERY_REASON_API',
    },
    // 取消配送
    '/v2/order/receive/processed/w/distribute/cancel/v2': {
        sampleRate: 1,
        metricsKey: 'CANCEL_DELIVERY_API',
    },
    // 二次确认取消配送
    '/v2/order/receive/processed/w/distribute/cancel/confirm/v2': {
        sampleRate: 1,
        metricsKey: 'CONFIRM_CANCEL_DELIVERY_API',
    },
    // 加小费/修改小费
    '/v2/order/receive/processed/w/distribute/tipAmount/v2': {
        sampleRate: 1,
        metricsKey: 'ADD_MODIFY_DELIVERY_TIPS_API',
    },
    // 取消订单前查询门店营业状态
    '/common/status': {
        sampleRate: 1,
        metricsKey: 'QUERY_BUSINESS_STATUS_BEFORE_CANCEL_ORDER_API',
    },
    // 查取消订单原因列表（外卖不用了）
    '/v2/order/receive/cancelReasons/r/get': {
        sampleRate: 1,
        metricsKey: 'QUERY_CANCEL_ORDER_REASON_API',
    },
    // 查取消订单原因列表-新
    '/gw/v2/order/receive/cancelReasons/r/get/new': {
        sampleRate: 1,
        metricsKey: 'QUERY_CANCEL_ORDER_REASON_API',
    },
    // 取消订单
    '/v2/order/receive/unprocessed/w/cancel': {
        sampleRate: 1,
        metricsKey: 'CANCEL_ORDER_API',
    },
};

const onFulfilled = (response: AxiosResponse) => {
    const reportConfig = REPORT_CONFIG_MAP[(response.config.url || '').replace(/\?.*$/, '')];
    if (
        reportConfig &&
        isObject(response.data) &&
        'code' in response.data &&
        Math.random() < reportConfig.sampleRate
    ) {
        // 上报HTTP成功与业务状态码
        reportCustomMetrics(reportConfig.metricsKey, 1, {
            ...(reportConfig.customTags || {}),
            http_success: 1,
            business_code: (response.data as any).code,
        });
    }

    return response;
};

interface ErrorFromAxios extends Error {
    response?: AxiosResponse;
}

const onRejected = (error: ErrorFromAxios) => {
    if (error.response) {
        const response = error.response;
        const reportConfig = REPORT_CONFIG_MAP[response.config.url || ''];
        if (reportConfig && Math.random() < reportConfig.sampleRate) {
            // 上报HTTP失败
            reportCustomMetrics(reportConfig.metricsKey, 1, {
                ...(reportConfig.customTags || {}),
                http_success: 0,
            });
        }
    }

    return Promise.reject(error);
};

export default {
    onFulfilled,
    onRejected,
};
